import { Email, Language, Phone } from '@mui/icons-material';
import {
  Box,
  Divider,
  Drawer,
  Grid,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  lighten,
  styled,
  useTheme,
} from '@mui/material';
import details from 'details.json';
import { forwardRef, useContext } from 'react';
import { NavLink as RouterLink, useNavigate } from 'react-router-dom';
import appConfig from 'src/config/appConfig';
import { useDynamicBrand } from 'src/context/DynamicBrandContext';
import { SidebarContext } from 'src/context/SidebarContext';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import useIsMobile from 'src/hooks/useIsMobile';
import { useOrganisation } from 'src/hooks/useOrganisation';
import Logo, { LogoVariant } from 'src/pages/Login/components/Logo';
import pages from 'src/router/routes';
import { getEllipsisTxt } from 'src/utils/formatters';
import { isAllSelection } from 'src/utils/selection';
import Scrollbar from 'switch-fe-shared/components/Scrollbar/Scrollbar';

import CollapsibleSidebarMenu from './components/CollapsibleSidebarMenu';
import SidebarToggleButton from './components/SidebarToggleButton';

interface SidebarWrapperProps {
  collapsed: boolean;
}

interface BrandLogoSectionProps {
  collapsed: boolean;
  showBrandName?: boolean;
  isMobile?: boolean;
}

const SidebarWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<SidebarWrapperProps>(
  ({ theme, collapsed }) => `
    width: ${collapsed ? '70px' : theme.sidebar.width};
    min-width: ${collapsed ? '70px' : theme.sidebar.width};
    color: ${theme.colors.alpha.trueWhite[70]};
    position: relative;
    z-index: 7;
    height: 100%;
    padding-bottom: 8px;
    border-right: 1px solid ${theme.palette.divider};
    transition: width 0.3s ease-in-out;
`
);

const CollapsibleSidebar = () => {
  /* Hooks & Use Effects */
  const { sidebarToggle, toggleSidebar, isCollapsed, toggleCollapsed } =
    useContext(SidebarContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const selectedGroupId = useEffectiveApplicationGroupId();
  const selectedAppId = useEffectiveApplicationId();
  const {
    neutralDashboardPath,
    getApplicationDashboardPath,
    getApplicationGroupDashboardPath,
  } = useAdminUrlNavigation();

  /* States */
  const effectiveCollapsed = isMobile || isCollapsed;

  /* API Calls */

  /* Functions & Functional Components */
  const closeSidebar = () => toggleSidebar();

  const { config: brandConfig } = useDynamicBrand();
  const brandFullName = brandConfig.brandName;
  const brandDetails = brandConfig.contactDetails
    ? brandConfig.contactDetails
    : {
        website: details?.website,
        email: details?.email,
        number: details?.number,
      };
  const dashboardPath = !isAllSelection(selectedAppId)
    ? getApplicationDashboardPath(selectedAppId, selectedGroupId)
    : selectedGroupId && !isAllSelection(selectedGroupId)
      ? getApplicationGroupDashboardPath(selectedGroupId)
      : neutralDashboardPath;

  const TooltipTitle = () =>
    brandDetails ? (
      <Grid container flexDirection={'column'}>
        {brandDetails.website && (
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to={`${brandDetails.website}`}
              target='_blank'
              sx={{
                transition: 'none',
                color: theme.tokens.colors.card,
                borderRadius: theme.tokens.borderRadius.md,
                '&:hover': {
                  backgroundColor: lighten(theme.colors.primary.main, 0.9),
                  color: theme.tokens.extendedColors.interactive.menuHoverText,
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                <Language sx={{ color: 'inherit' }} />
              </ListItemIcon>
              <ListItemText primary={brandDetails.website} />
            </ListItemButton>
          </ListItem>
        )}

        {brandDetails.email && (
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to={`mailto:${brandDetails.email}`}
              sx={{
                transition: 'none',
                color: theme.tokens.colors.card,
                borderRadius: theme.tokens.borderRadius.md,
                '&:hover': {
                  backgroundColor: lighten(theme.colors.primary.main, 0.9),
                  color: theme.tokens.extendedColors.interactive.menuHoverText,
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                <Email sx={{ color: 'inherit' }} />
              </ListItemIcon>
              <ListItemText primary={brandDetails.email} />
            </ListItemButton>
          </ListItem>
        )}

        {brandDetails.number && (
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to={`tel:${brandDetails.number}`}
              sx={{
                transition: 'none',
                color: theme.tokens.colors.card,
                borderRadius: theme.tokens.borderRadius.md,
                '&:hover': {
                  backgroundColor: lighten(theme.colors.primary.main, 0.9),
                  color: theme.tokens.extendedColors.interactive.menuHoverText,
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                <Phone sx={{ color: 'inherit' }} />
              </ListItemIcon>
              <ListItemText primary={brandDetails.number} />
            </ListItemButton>
          </ListItem>
        )}
      </Grid>
    ) : undefined;

  const BrandLogoSection = forwardRef<HTMLDivElement, BrandLogoSectionProps>(
    ({ collapsed, showBrandName = true, isMobile = false }, ref) => {
      const environment = useOrganisation();

      return (
        <Box
          ref={ref}
          onClick={() => navigate(dashboardPath)}
          mx={collapsed ? 1 : 2}
          mb={1}
          sx={{
            width: collapsed ? (isMobile ? '100%' : '50px') : '100%',
            justifyContent: collapsed ? 'center' : 'flex-start',
            alignItems: 'center',
            cursor: 'pointer',
            display: 'flex',
            gap: 2,
            flexDirection: 'row',
            fontFamily: theme.tokens.typography.fontFamily.display,
          }}
        >
          <Logo height={40} variant={LogoVariant.PRIMARY} />
          {showBrandName && (!collapsed || isMobile) && (
            <Box
              component='span'
              sx={{
                color: theme.palette.text.primary,
                fontSize: '16px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                opacity: collapsed && !isMobile ? 0 : 1,
                transition: 'opacity 0.2s ease-in-out',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '180px',
                textAlign: 'left',
              }}
            >
              {brandFullName.length > 20
                ? getEllipsisTxt(brandFullName, 20)
                : brandFullName}
            </Box>
          )}
        </Box>
      );
    }
  );

  return (
    <>
      <SidebarWrapper
        collapsed={effectiveCollapsed}
        sx={{
          display: {
            xs: 'none',
            [appConfig.breakMobileView]: 'inline-block',
          },
          position: 'fixed',
          left: 0,
          top: 0,
          background:
            theme.palette.mode === 'dark'
              ? lighten(theme.sidebar.background ?? '#111633', 0.1)
              : theme.sidebar.background,
          boxShadow:
            theme.palette.mode === 'dark' ? theme.sidebar.boxShadow : 'none',
        }}
      >
        {!isMobile && (
          <SidebarToggleButton
            isCollapsed={effectiveCollapsed}
            onToggle={toggleCollapsed}
          />
        )}

        <Grid container flexDirection={'column'} height={'100%'}>
          <Grid item marginTop={3}>
            <Tooltip enterDelay={700} title={<TooltipTitle />}>
              <span>
                <BrandLogoSection collapsed={effectiveCollapsed} />
              </span>
            </Tooltip>
          </Grid>
          <Grid item flex={1}>
            <CollapsibleSidebarMenu isCollapsed={effectiveCollapsed} />
          </Grid>
        </Grid>
      </SidebarWrapper>

      {/* Mobile Drawer - unchanged */}
      <Drawer
        sx={{
          boxShadow: `${theme.sidebar.boxShadow}`,
        }}
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant='temporary'
        elevation={9}
      >
        <SidebarWrapper
          collapsed={false}
          sx={{
            background:
              theme.palette.mode === 'dark'
                ? lighten(theme.sidebar.background ?? '#111633', 0.1)
                : theme.sidebar.background,
            boxShadow:
              theme.palette.mode === 'dark' ? theme.sidebar.boxShadow : 'none',
          }}
        >
          <Scrollbar>
            <Box mt={3}>
              <Tooltip enterDelay={700} title={<TooltipTitle />}>
                <BrandLogoSection collapsed={false} showBrandName isMobile />
              </Tooltip>
            </Box>
            <Divider
              sx={{
                mt: theme.spacing(3),
                mx: theme.spacing(2),
                background: theme.colors.alpha.trueWhite[10],
              }}
            />
            <CollapsibleSidebarMenu isCollapsed={false} />
          </Scrollbar>
        </SidebarWrapper>
      </Drawer>
    </>
  );
};

export default CollapsibleSidebar;
